import { bootstrapFromOneWitysk, clearAccessToken, currentAccessToken, forceBootstrapFromOneWitysk, getAccessToken } from "./auth";
import { embedActive } from "./embed";

export interface AnonTokenResponse {
  livekit_url: string;
  token: string;
  room_name: string;
  ice_servers: {
    username: string;
    credential: string;
    urls: string[];
    ttl: number;
  } | null;
}

export interface WaitingResponse {
  status: "waiting";
  wait_token: string;
  room_name: string;
}

export type AnonTokenOrWait = AnonTokenResponse | WaitingResponse;

export interface PendingJoiner {
  wait_token: string;
  display_name: string;
  email: string | null;
  requested_at: number;
}

export interface WaitPollOut {
  status: "pending" | "admitted" | "denied" | "unknown";
  livekit_url?: string | null;
  token?: string | null;
  ice_servers?: AnonTokenResponse["ice_servers"];
  room_name?: string | null;
}

export interface PollDTO {
  id: string;
  meeting_id: string;
  question: string;
  options: string[];
  counts: number[];
  total_votes: number;
  status: "open" | "closed";
  created_at: string | null;
  closed_at: string | null;
}

export interface WhiteboardShapeDTO {
  id: string;
  kind: "rect" | "ellipse" | "text";
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
  stroke_width: number;
  text: string | null;
  font_size: number | null;
}

export interface QuestionDTO {
  id: number;
  meeting_id: string;
  asker_identity: string;
  asker_name: string;
  question: string;
  status: "open" | "answered" | "dismissed";
  upvotes: number;
  created_at: string | null;
  answered_at: string | null;
}

export interface MeetingOut {
  id: string;
  room_name: string;
  display_title: string;
  owner_user_id: string;
  owner_name?: string | null;
  is_active: boolean;
  require_password: boolean;
  max_participants: number;
  branding_url?: string | null;
  list_for_authenticated?: boolean;
  list_for_anonymous?: boolean;
  livestream_enabled?: boolean;
  livestream_rtmps_url?: string | null;
  livestream_stream_key?: string | null;
  livestream_substack_enabled?: boolean;
  livestream_substack_rtmps_url?: string | null;
  livestream_substack_stream_key?: string | null;
  livestream_youtube_enabled?: boolean;
  livestream_youtube_rtmps_url?: string | null;
  livestream_youtube_stream_key?: string | null;
  livestream_youtube_mode?: "rtmp" | "api";
  livestream_youtube_oauth_connected?: boolean;
  livestream_youtube_channel_title?: string | null;
  livestream_youtube_watch_url?: string | null;
  livestream_facebook_enabled?: boolean;
  livestream_facebook_rtmps_url?: string | null;
  livestream_facebook_stream_key?: string | null;
  livestream_rumble_enabled?: boolean;
  livestream_rumble_rtmps_url?: string | null;
  livestream_rumble_stream_key?: string | null;
  livestream_active?: boolean;
  current_egress_layout?: string | null;
  playback_enabled?: boolean;
  playback_loop?: boolean;
  playback_whats_up_next?: boolean;
  playback_active?: boolean;
  playback_current_item_id?: string | null;
  pip_enabled?: boolean;
  pip_overlay_identity?: string | null;
  // Room-wide composition layout. The SPA uses this as initial state and
  // listens for LiveKit RoomMetadataChanged for live updates.
  room_layout?: "single-speaker" | "speaker" | "grid";
  public_enabled?: boolean;
  public_slug?: string | null;
  public_url?: string | null;
  // Mail integration (meet-integration.md §4.1).
  join_url?: string | null;
  allow_anonymous?: boolean;
  has_password?: boolean;
  external_source?: string | null;
  external_ref?: string | null;
}

export interface CameraPublisher {
  identity: string;
  name: string;
}

export interface PlaybackItemOut {
  id: string;
  position: number;
  filename: string;
  file_size_bytes: number;
  mime_type: string;
  uploaded_at: string | null;
  duration_seconds: number | null;
  // When non-null this row is an ALIAS: it has no file of its own and
  // resolves to the source row's file at playback time. file_size_bytes
  // is 0 on alias rows (the source's size is canonical).
  source_item_id: string | null;
}

export interface PlaybackStateOut {
  enabled: boolean;
  loop: boolean;
  active: boolean;
  current_item_id: string | null;
  current_item_filename: string | null;
  current_item_duration_seconds: number | null;
  started_at: string | null;
  // True while a freeze-frame ingress is holding on a single frame.
  paused: boolean;
  // Offset (seconds into the current item) the freeze-frame is showing;
  // the SPA pins the progress bar here while paused.
  paused_offset_seconds: number | null;
}

export interface PublicMeeting {
  room_name: string;
  display_title: string;
  max_participants: number;
  require_password: boolean;
  branding_url: string | null;
  owner_name: string | null;
  public_enabled?: boolean;
  public_slug?: string | null;
  public_url?: string | null;
  // Computed by the API per-caller. True when the viewer (anonymous or
  // authenticated) is allowed to actually join the meeting; false when
  // the row is in Discover only because of `public_enabled` (View only).
  joinable?: boolean;
}

export interface OnDemandVideo {
  id: string;
  filename: string;
  duration_seconds: number;
  // No-auth streaming path (relative), e.g. /api/v1/on-demand/items/<id>.
  stream_url: string;
}

export interface OnDemandMeeting {
  room_name: string;
  display_title: string;
  public_slug: string;
  owner_name: string | null;
  branding_url: string | null;
  videos: OnDemandVideo[];
}

export interface PublicRoomInfoForViewer {
  room_name: string;
  display_title: string;
  owner_name: string | null;
  branding_url: string | null;
  public_slug: string;
}

export interface PublicViewerTokenResponse {
  livekit_url: string;
  token: string;
  room_name: string;
  display_title: string;
  branding_url: string | null;
  ice_servers: AnonTokenResponse["ice_servers"];
}

export interface PublicRoomInfo {
  room_name: string;
  display_title: string;
  require_password: boolean;
  branding_url: string | null;
  owner_name: string | null;
  lobby_greeting?: string | null;
  meeting_id?: string;
}

export interface UserPreferencesOut {
  language: string | null;
  language_set_manually: boolean;
  anonymise_email_in_join_log?: boolean;
  dont_log_my_ip?: boolean;
}

export interface MeOut {
  id: number;
  // "mail": a One Voice Mail user without a linked One Voice account
  // (meet-integration.md §3.2).
  kind: "sso" | "native" | "mail";
  external_id: string | null;
  email: string | null;
  username: string | null;
  name: string | null;
  facepic_path: string | null;
  is_admin: boolean;
  is_voucher_admin: boolean;
  is_platform_admin: boolean;
  trial_used: boolean;
  trial_days_remaining: number | null;
  entitlement_kind: string | null;
  entitlement_expires_at: string | null;
  totp_enabled: boolean;
  totp_recovery_remaining: number;
  email_otp_enabled: boolean;
}

// ─── Admin panel ──────────────────────────────────────────────────────

export interface AdminUserOut {
  id: number;
  kind: "sso" | "native";
  external_id: string | null;
  email: string | null;
  username: string | null;
  name: string | null;
  is_admin: boolean;
  is_platform_admin: boolean;
  is_disabled: boolean;
  disable_reason: string | null;
  trial_used: boolean;
  entitlement_kind: string | null;
  entitlement_expires_at: string | null;
  totp_enabled: boolean;
  email_otp_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface AdminUserList {
  total: number;
  users: AdminUserOut[];
}

export interface BlockedIPOut {
  id: number;
  ip_address: string;
  reason: string | null;
  blocked_by_user_id: number | null;
  block_count: number;
  is_enabled: boolean;
  created_at: string;
  live_hits: number;
}

export interface IdsTempBlock {
  ip: string;
  expires_at: string;
  seconds_remaining: number;
  hits: number;
}

export interface IdsStatusOut {
  enabled: boolean;
  tracked_ips: number;
  temp_blocked: number;
  events_in_memory: number;
  temp_blocks: IdsTempBlock[];
}

export interface IdsEvent {
  ts: string | null;
  event_type: string;
  severity: string;
  ip: string | null;
  user_id: number | null;
  handle: string | null;
  path: string | null;
  user_agent: string | null;
  details: string | null;
}

export type LoginResult =
  | { kind: "ok"; access_token: string; user: MeOut }
  | {
      kind: "2fa";
      challenge_token: string;
      totp_enabled: boolean;
      email_otp_enabled: boolean;
      // Set when the backend already auto-mailed a fresh code on login (i.e.
      // the user's only second factor is email). Masked address for display.
      email_otp_sent_to: string | null;
    };

export interface ChatReactionDTO {
  emoji: string;
  reactor_identity: string;
  reactor_name: string;
}

export interface ChatMessageDTO {
  id: number;
  sender_identity: string;
  sender_name: string;
  message: string;
  reply_to_id: number | null;
  sent_at: string;
  pinned_at?: string | null;
  pinned_by?: string | null;
  attachment: {
    url: string;
    type: string | null;
    name: string | null;
    size: number | null;
  } | null;
  reactions: ChatReactionDTO[];
}

export const CHAT_REACTIONS = ["😊", "👍", "😂", "😢", "😠", "🤓", "❤️", "👎"] as const;
export type ChatReactionEmoji = (typeof CHAT_REACTIONS)[number];

export interface BillingHistoryItem {
  date: string;
  kind:
    | "paypal_order_monthly"
    | "paypal_order_annual"
    | "paypal_subscription_monthly"
    | "paypal_subscription_annual"
    | "voucher";
  label: string;
  amount: string | null;
  currency: string | null;
  status: string | null;
  reference: string | null;
}

export interface VoucherOut {
  id: number;
  code: string;
  duration_days: number;
  note: string | null;
  issued_by: string;
  redeemed_by_user_id: number | null;
  redeemed_at: string | null;
  created_at: string;
  expires_at: string;
}

async function fetchOnce(path: string, init: RequestInit, token: string | null): Promise<Response> {
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  return fetch(path, { ...init, headers });
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const used = await currentAccessToken();
  let res = await fetchOnce(path, init, used);

  // On ANY 401, try one silent SSO re-bootstrap and retry. This used to be
  // gated on the 401 detail matching /expired|invalid token/, which missed
  // the most common real-world case: getAccessToken() had already CLEARED
  // the expired token, the request went out with no Authorization header at
  // all, and the 401 detail was "missing Authorization header" — no match,
  // no retry, and the user saw an auth error mid-meeting (e.g. pressing
  // Stop recording after a long session). The force variant is required
  // for the same reason: the non-force bootstrap short-circuits on any
  // cached token, which is exactly the token that just got rejected.
  if (res.status === 401) {
    // Embedded: calls that went out with the same stale token 401 one after
    // another; once Mail sent a new token, retry with it instead of clearing
    // it and asking again.
    const renewed = embedActive() ? getAccessToken() : null;
    let fresh: string | null;
    if (renewed && renewed !== used) {
      fresh = renewed;
    } else {
      clearAccessToken();
      fresh = await forceBootstrapFromOneWitysk();
    }
    if (fresh) {
      res = await fetchOnce(path, init, fresh);
    }
  }

  if (!res.ok) {
    let detail = res.statusText;
    try {
      const data = await res.json();
      detail = data.detail ?? detail;
    } catch {
      /* not JSON */
    }
    throw new Error(detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  createMeeting: (body: {
    display_title: string;
    password?: string;
    list_for_authenticated?: boolean;
    list_for_anonymous?: boolean;
    allow_anonymous?: boolean;
    display_name?: string | null;
    auto_admit_authenticated?: boolean;
    require_name_on_join?: boolean;
    auto_mute_new_joiners?: boolean;
    auto_disable_camera_for_new?: boolean;
    waiting_room_enabled?: boolean;
    lock_room_after_start?: boolean;
    allow_participant_screenshare?: boolean;
    allow_participant_chat?: boolean;
    lobby_greeting?: string | null;
    recurrence_rule?: string | null;
    duration_minutes?: number | null;
    livestream_enabled?: boolean;
    livestream_rtmps_url?: string | null;
    livestream_stream_key?: string | null;
    livestream_substack_enabled?: boolean;
    livestream_substack_rtmps_url?: string | null;
    livestream_substack_stream_key?: string | null;
    livestream_youtube_enabled?: boolean;
    livestream_youtube_rtmps_url?: string | null;
    livestream_youtube_stream_key?: string | null;
    livestream_facebook_enabled?: boolean;
    livestream_facebook_rtmps_url?: string | null;
    livestream_facebook_stream_key?: string | null;
    livestream_rumble_enabled?: boolean;
    livestream_rumble_rtmps_url?: string | null;
    livestream_rumble_stream_key?: string | null;
  }) =>
    request<{ meeting: MeetingOut; join_url: string }>("/api/v1/meetings", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateMeeting: (
    meetingId: string,
    body: {
      display_title?: string;
      list_for_authenticated?: boolean;
      list_for_anonymous?: boolean;
      allow_anonymous?: boolean;
      recording_mode?: "manual" | "auto_on_start" | "off";
      livestream_enabled?: boolean;
      livestream_rtmps_url?: string | null;
      livestream_stream_key?: string | null;
      livestream_substack_enabled?: boolean;
      livestream_substack_rtmps_url?: string | null;
      livestream_substack_stream_key?: string | null;
      livestream_youtube_enabled?: boolean;
      livestream_youtube_rtmps_url?: string | null;
      livestream_youtube_stream_key?: string | null;
      livestream_youtube_mode?: "rtmp" | "api";
      livestream_facebook_enabled?: boolean;
      livestream_facebook_rtmps_url?: string | null;
      livestream_facebook_stream_key?: string | null;
      livestream_rumble_enabled?: boolean;
      livestream_rumble_rtmps_url?: string | null;
      livestream_rumble_stream_key?: string | null;
      playback_enabled?: boolean;
      playback_loop?: boolean;
      playback_whats_up_next?: boolean;
      pip_enabled?: boolean;
      pip_overlay_identity?: string | null;
      public_enabled?: boolean;
      public_slug?: string | null;
    }
  ) =>
    request<MeetingOut>(`/api/v1/meetings/${meetingId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  listCameraPublishers: (meetingId: string) =>
    request<CameraPublisher[]>(
      `/api/v1/meetings/${meetingId}/camera-publishers`,
    ),

  listMeetings: () => request<MeetingOut[]>("/api/v1/meetings"),

  getMeeting: (meetingId: string) =>
    request<MeetingOut>(`/api/v1/meetings/${meetingId}`),

  listDiscoverable: () => request<PublicMeeting[]>("/api/v1/discoverable"),

  listPublicMeetings: () => request<PublicMeeting[]>("/api/v1/public-meetings"),

  // No-auth: active public view-only streams (public_enabled + public_slug).
  // Powers the "Public livestreams" subsection of the Discover panel, shown
  // to signed-out visitors too.
  listPublicStreams: () => request<PublicMeeting[]>("/api/v1/public-streams"),

  // No-auth: On Demand catalogue — ongoing public-livestream meetings whose
  // playlists contain videos longer than 5 minutes. One entry per meeting.
  listOnDemand: () => request<OnDemandMeeting[]>("/api/v1/on-demand"),

  ownerToken: (meetingId: string, body: { display_name?: string | null } = {}) =>
    request<AnonTokenResponse & { role?: "owner" | "cohost" }>(
      `/api/v1/meetings/${meetingId}/token`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  myRoleInRoom: (roomName: string) =>
    request<{ role: "owner" | "cohost" | "guest"; meeting_id: string }>(
      `/api/v1/rooms/${roomName}/me-role`,
    ),

  listCohosts: (meetingId: string) =>
    request<string[]>(`/api/v1/meetings/${meetingId}/cohosts`),

  addCohost: (meetingId: string, userSub: string) =>
    request<{ ok: boolean; cohosts: string[] }>(`/api/v1/meetings/${meetingId}/cohosts`, {
      method: "POST",
      body: JSON.stringify({ user_sub: userSub }),
    }),

  removeCohost: (meetingId: string, userSub: string) =>
    request<{ ok: boolean; cohosts: string[] }>(
      `/api/v1/meetings/${meetingId}/cohosts/${encodeURIComponent(userSub)}`,
      { method: "DELETE" },
    ),

  anonToken: (roomName: string, body: { display_name: string; email?: string; password?: string }) =>
    request<AnonTokenOrWait>(`/api/v1/rooms/${roomName}/anon-token`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  pollWait: (roomName: string, waitToken: string) =>
    request<WaitPollOut>(`/api/v1/rooms/${roomName}/wait/${waitToken}`),

  // ─── Shared notes ──────────────────────────────────────────────
  getNotes: (roomName: string) =>
    request<{ notes: string; meeting_id: string }>(`/api/v1/rooms/${roomName}/notes`),
  putNotes: (roomName: string, notes: string) =>
    request<{ ok: boolean; length: number }>(`/api/v1/rooms/${roomName}/notes`, {
      method: "PUT",
      body: JSON.stringify({ notes }),
    }),

  // ─── Shared whiteboard ─────────────────────────────────────────
  getWhiteboardStrokes: (roomName: string) =>
    request<Array<Record<string, unknown>>>(
      `/api/v1/rooms/${roomName}/whiteboard/strokes`,
    ),
  postWhiteboardStroke: (roomName: string, packet: Record<string, unknown>) =>
    request<{ ok: boolean }>(`/api/v1/rooms/${roomName}/whiteboard/strokes`, {
      method: "POST",
      body: JSON.stringify({ packet }),
    }),
  clearWhiteboardStrokes: (roomName: string) =>
    request<{ ok: boolean }>(`/api/v1/rooms/${roomName}/whiteboard/strokes`, {
      method: "DELETE",
    }),
  listWhiteboardShapes: (roomName: string) =>
    request<WhiteboardShapeDTO[]>(`/api/v1/rooms/${roomName}/whiteboard/shapes`),
  upsertWhiteboardShape: (roomName: string, shape: WhiteboardShapeDTO) => {
    const { id, ...body } = shape;
    return request<{ ok: boolean }>(
      `/api/v1/rooms/${roomName}/whiteboard/shapes/${id}`,
      { method: "PUT", body: JSON.stringify(body) },
    );
  },
  deleteWhiteboardShape: (roomName: string, shapeId: string) =>
    request<{ ok: boolean }>(
      `/api/v1/rooms/${roomName}/whiteboard/shapes/${shapeId}`,
      { method: "DELETE" },
    ),

  // ─── Polls + Q&A ───────────────────────────────────────────────
  listPolls: (meetingId: string) =>
    request<PollDTO[]>(`/api/v1/meetings/${meetingId}/polls`),
  createPoll: (meetingId: string, body: { question: string; options: string[] }) =>
    request<PollDTO>(`/api/v1/meetings/${meetingId}/polls`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  votePoll: (pollId: string, voterIdentity: string, optionIndex: number) =>
    request<PollDTO>(`/api/v1/polls/${pollId}/vote`, {
      method: "POST",
      body: JSON.stringify({ voter_identity: voterIdentity, option_index: optionIndex }),
    }),
  closePoll: (pollId: string) =>
    request<PollDTO>(`/api/v1/polls/${pollId}/close`, { method: "POST" }),
  listQuestions: (meetingId: string) =>
    request<QuestionDTO[]>(`/api/v1/meetings/${meetingId}/questions`),
  askQuestion: (
    meetingId: string,
    body: { asker_identity: string; asker_name: string; question: string },
  ) =>
    request<QuestionDTO>(`/api/v1/meetings/${meetingId}/questions`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  upvoteQuestion: (questionId: number, voterIdentity: string) =>
    request<QuestionDTO>(`/api/v1/questions/${questionId}/upvote`, {
      method: "POST",
      body: JSON.stringify({ voter_identity: voterIdentity }),
    }),
  answerQuestion: (questionId: number) =>
    request<QuestionDTO>(`/api/v1/questions/${questionId}/answer`, { method: "POST" }),
  dismissQuestion: (questionId: number) =>
    request<QuestionDTO>(`/api/v1/questions/${questionId}`, { method: "DELETE" }),

  postFeedback: (
    roomName: string,
    body: {
      rating: number;
      comment?: string;
      participant_identity?: string | null;
      participant_name?: string | null;
    },
  ) =>
    request<{ ok: boolean }>(`/api/v1/rooms/${roomName}/feedback`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  listPendingJoiners: (meetingId: string) =>
    request<PendingJoiner[]>(`/api/v1/meetings/${meetingId}/pending`),

  admitPending: (meetingId: string, waitToken: string) =>
    request<{ ok: boolean; status: string }>(
      `/api/v1/meetings/${meetingId}/pending/${waitToken}/admit`,
      { method: "POST" }
    ),

  denyPending: (meetingId: string, waitToken: string) =>
    request<{ ok: boolean; status: string }>(
      `/api/v1/meetings/${meetingId}/pending/${waitToken}/deny`,
      { method: "POST" }
    ),

  mute: (meetingId: string, body: { participant_identity: string; mute: boolean }) =>
    request(`/api/v1/meetings/${meetingId}/mute`, { method: "POST", body: JSON.stringify(body) }),

  lowerHand: (meetingId: string, participantIdentity: string) =>
    request<{ ok: boolean }>(`/api/v1/meetings/${meetingId}/lower-hand`, {
      method: "POST",
      body: JSON.stringify({ participant_identity: participantIdentity }),
    }),

  muteAll: (meetingId: string) =>
    request(`/api/v1/meetings/${meetingId}/mute-all`, { method: "POST" }),

  kick: (meetingId: string, participant_identity: string) =>
    request(`/api/v1/meetings/${meetingId}/kick`, {
      method: "POST",
      body: JSON.stringify({ participant_identity }),
    }),

  endMeeting: (meetingId: string) =>
    request(`/api/v1/meetings/${meetingId}`, { method: "DELETE" }),

  deleteMeeting: (meetingId: string) =>
    request(`/api/v1/meetings/${meetingId}`, { method: "DELETE" }),

  reopenMeeting: (meetingId: string) =>
    request<MeetingOut>(`/api/v1/meetings/${meetingId}/reopen`, { method: "POST" }),

  // Café — always-on audio room. The token bears `room: "ti-cafe"`; LiveKit
  // auto-creates the room on first join.
  tiCafeToken: () =>
    request<AnonTokenResponse>("/api/v1/ti-cafe/token", { method: "POST" }),

  tiCafeLive: () =>
    request<{ user_ids: number[] }>("/api/v1/ti-cafe/live"),

  setRoomLayout: (
    meetingId: string,
    layout: "single-speaker" | "speaker" | "grid",
  ) =>
    request<{ ok: boolean; room_layout: string }>(
      `/api/v1/meetings/${meetingId}/layout`,
      { method: "POST", body: JSON.stringify({ layout }) },
    ),

  setPresenter: (meetingId: string, participant_identity: string | null) =>
    request(`/api/v1/meetings/${meetingId}/presenter`, {
      method: "POST",
      body: JSON.stringify({ participant_identity }),
    }),

  // ─── Native auth ─────────────────────────────────────────────────────
  signup: (body: { email: string; username: string; password: string; name?: string | null }) =>
    request<{ access_token: string; user: MeOut }>("/api/v1/auth/signup", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Login may return either a normal access token + user, or, when the
   * account has 2FA enabled, a short-lived challenge token that must be
   * exchanged via `loginVerify2fa` together with a TOTP code. The wrapper
   * normalises both shapes into a discriminated union. */
  async login(body: { handle: string; password: string }): Promise<LoginResult> {
    const r = await request<
      | { access_token: string; user: MeOut }
      | {
          requires_2fa: true;
          challenge_token: string;
          totp_enabled: boolean;
          email_otp_enabled: boolean;
          email_otp_sent_to: string | null;
        }
    >("/api/v1/auth/login", { method: "POST", body: JSON.stringify(body) });
    if ("requires_2fa" in r && r.requires_2fa) {
      return {
        kind: "2fa",
        challenge_token: r.challenge_token,
        totp_enabled: r.totp_enabled,
        email_otp_enabled: r.email_otp_enabled,
        email_otp_sent_to: r.email_otp_sent_to,
      };
    }
    const ok = r as { access_token: string; user: MeOut };
    return { kind: "ok", access_token: ok.access_token, user: ok.user };
  },

  loginVerify2fa: (body: { challenge_token: string; code: string }) =>
    request<{ access_token: string; user: MeOut }>("/api/v1/auth/login/2fa", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  loginSendEmailOtp: (challenge_token: string) =>
    request<{ ok: boolean; sent_to: string }>("/api/v1/auth/login/email-otp/send", {
      method: "POST",
      body: JSON.stringify({ challenge_token }),
    }),

  logout: () => request<{ ok: boolean }>("/api/v1/auth/logout", { method: "POST" }),

  me: () => request<MeOut>("/api/v1/me"),

  updateMe: (body: { name?: string | null; email?: string | null; username?: string | null }) =>
    request<MeOut>("/api/v1/me", { method: "PATCH", body: JSON.stringify(body) }),

  changePassword: (body: { current_password: string; new_password: string }) =>
    request<{ ok: boolean }>("/api/v1/me/password", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Native-user facepic upload — multipart, image-only, 5 MB cap server-side. */
  async uploadFacepic(file: File): Promise<MeOut> {
    const tok = getAccessToken();
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch("/api/v1/me/facepic", {
      method: "POST",
      headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      body: fd,
    });
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const j = await res.clone().json();
        detail = j.detail ?? detail;
      } catch {
        /* not JSON */
      }
      throw new Error(detail);
    }
    return (await res.json()) as MeOut;
  },

  deleteFacepic: () => request<MeOut>("/api/v1/me/facepic", { method: "DELETE" }),

  // ─── Vouchers ────────────────────────────────────────────────────────
  issueVoucher: (body: { duration_days: number; note?: string | null }) =>
    request<VoucherOut>("/api/v1/vouchers", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  listVouchers: () => request<VoucherOut[]>("/api/v1/vouchers"),

  revokeVoucher: (code: string) =>
    request<{ ok: boolean; revoked_user_id: number | null }>(
      `/api/v1/vouchers/${encodeURIComponent(code)}`,
      { method: "DELETE" }
    ),

  redeemVoucher: (code: string) =>
    request<{ ok: boolean; duration_days: number; entitlement_expires_at: string }>(
      "/api/v1/vouchers/redeem",
      { method: "POST", body: JSON.stringify({ code }) }
    ),

  // ─── Password reset + account deletion ──────────────────────────────
  requestPasswordReset: (email: string) =>
    request<{ ok: boolean }>("/api/v1/auth/password-reset/request", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  confirmPasswordReset: (token: string, new_password: string) =>
    request<{ ok: boolean }>("/api/v1/auth/password-reset/confirm", {
      method: "POST",
      body: JSON.stringify({ token, new_password }),
    }),

  deleteMyAccount: (password: string) =>
    request<{ ok: boolean }>("/api/v1/me", {
      method: "DELETE",
      body: JSON.stringify({ password }),
    }),

  startTrial: () => request<MeOut>("/api/v1/me/start-trial", { method: "POST" }),

  // ─── 2FA (TOTP) ─────────────────────────────────────────────────────
  totpSetup: () =>
    request<{ secret: string; otpauth_uri: string }>("/api/v1/me/2fa/setup", { method: "POST" }),

  totpEnable: (code: string) =>
    request<{ ok: boolean; recovery_codes: string[] }>("/api/v1/me/2fa/enable", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),

  totpDisable: (body: { password: string; code: string }) =>
    request<{ ok: boolean }>("/api/v1/me/2fa/disable", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  totpRegenerateRecovery: (code: string) =>
    request<{ ok: boolean; recovery_codes: string[] }>("/api/v1/me/2fa/recovery-regenerate", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),

  emailOtpStart: () =>
    request<{ ok: boolean; sent_to: string }>("/api/v1/me/2fa/email/start", { method: "POST" }),

  emailOtpConfirm: (code: string) =>
    request<{ ok: boolean }>("/api/v1/me/2fa/email/confirm", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),

  emailOtpDisable: (password: string) =>
    request<{ ok: boolean }>("/api/v1/me/2fa/email/disable", {
      method: "POST",
      body: JSON.stringify({ password }),
    }),

  // ─── PayPal billing ─────────────────────────────────────────────────
  billingConfig: () =>
    request<{
      enabled: boolean;
      client_id: string;
      plan_id_monthly: string;
      plan_id_annual: string;
      monthly_price: string;
      monthly_currency: string;
      annual_price: string;
      annual_currency: string;
    }>("/api/v1/billing/config"),

  createPaypalOrder: (kind: "monthly" | "annual" = "annual") =>
    request<{ order_id: string; kind: string }>("/api/v1/billing/orders", {
      method: "POST",
      body: JSON.stringify({ kind }),
    }),

  capturePaypalOrder: (orderId: string) =>
    request<{ ok: boolean; status?: string; already_captured?: boolean; kind?: string }>(
      `/api/v1/billing/orders/${encodeURIComponent(orderId)}/capture`,
      { method: "POST" }
    ),

  activatePaypalSubscription: (subscriptionId: string, plan: "monthly" | "annual" = "monthly") =>
    request<{ ok: boolean; status: string; plan: string }>("/api/v1/billing/subscriptions/activated", {
      method: "POST",
      body: JSON.stringify({ subscription_id: subscriptionId, plan }),
    }),

  cancelPaypalSubscription: () =>
    request<{ ok: boolean }>("/api/v1/billing/subscriptions/cancel", { method: "POST" }),

  myBillingHistory: () =>
    request<BillingHistoryItem[]>("/api/v1/billing/me/billing-history"),

  listChat: (roomName: string) =>
    request<ChatMessageDTO[]>(`/api/v1/rooms/${roomName}/chat`),

  /** Owner-only — fetch a closed (or active) meeting's chat by id, for the
   * post-meeting transcript view from MyMeetings. */
  listMeetingChat: (meetingId: string) =>
    request<ChatMessageDTO[]>(`/api/v1/meetings/${meetingId}/chat`),

  postChat: (
    roomName: string,
    body: {
      sender_identity: string;
      sender_name: string;
      message: string;
      reply_to_id?: number | null;
    }
  ) =>
    request<ChatMessageDTO>(`/api/v1/rooms/${roomName}/chat`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Multipart attachment upload — image-only, 5 MB cap server-side. */
  async postChatAttachment(
    roomName: string,
    fields: {
      sender_identity: string;
      sender_name: string;
      message?: string;
      reply_to_id?: number | null;
      file: File;
    }
  ): Promise<ChatMessageDTO> {
    const fd = new FormData();
    fd.append("sender_identity", fields.sender_identity);
    fd.append("sender_name", fields.sender_name);
    if (fields.message) fd.append("message", fields.message);
    if (fields.reply_to_id != null) fd.append("reply_to_id", String(fields.reply_to_id));
    fd.append("file", fields.file);
    const res = await fetch(`/api/v1/rooms/${roomName}/chat/attachment`, {
      method: "POST",
      body: fd,
    });
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const j = await res.clone().json();
        detail = j.detail ?? detail;
      } catch {
        /* not JSON */
      }
      throw new Error(detail);
    }
    return (await res.json()) as ChatMessageDTO;
  },

  putChatReaction: (
    roomName: string,
    messageId: number,
    body: { reactor_identity: string; reactor_name: string; emoji: string }
  ) =>
    request<{ ok: boolean }>(
      `/api/v1/rooms/${roomName}/chat/${messageId}/reaction`,
      { method: "PUT", body: JSON.stringify(body) }
    ),

  deleteChatReaction: (roomName: string, messageId: number, reactorIdentity: string) =>
    request<void>(
      `/api/v1/rooms/${roomName}/chat/${messageId}/reaction?reactor_identity=${encodeURIComponent(
        reactorIdentity
      )}`,
      { method: "DELETE" }
    ),

  pinChatMessage: (meetingId: string, messageId: number, pinned: boolean) =>
    request<{ ok: boolean; pinned_at: string | null }>(
      `/api/v1/meetings/${meetingId}/chat/pin`,
      { method: "POST", body: JSON.stringify({ message_id: messageId, pinned }) },
    ),

  startRecording: (
    meetingId: string,
    body: { layout?: "speaker" | "grid" | "single-speaker" } = {}
  ) =>
    request(`/api/v1/meetings/${meetingId}/recordings:start`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  stopRecording: (meetingId: string) =>
    request(`/api/v1/meetings/${meetingId}/recordings:stop`, { method: "POST" }),

  startStream: (
    meetingId: string,
    body: { layout?: "speaker" | "grid" | "single-speaker" } = {},
  ) =>
    request<{ ok: boolean; egress_id: string }>(
      `/api/v1/meetings/${meetingId}/stream:start`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  stopStream: (meetingId: string) =>
    request<{ ok: boolean }>(`/api/v1/meetings/${meetingId}/stream:stop`, { method: "POST" }),

  streamStatus: (meetingId: string) =>
    request<{ enabled: boolean; active: boolean; egress_id: string | null }>(
      `/api/v1/meetings/${meetingId}/stream`,
    ),

  /** Per-destination RTMP publish status — one entry per enabled
   *  platform. Status drives the green/red/grey dots in the
   *  LivestreamSettingsModal so the host can see at a glance which
   *  destination is broken (and the exact error string from egress). */
  streamDestinations: (meetingId: string) =>
    request<
      Array<{
        platform_id: "x" | "substack" | "youtube" | "facebook" | "rumble";
        status: "idle" | "streaming" | "failed" | "complete";
        error: string | null;
        updated_at: string | null;
        // YouTube API-mode only — supervisor polls this every ~30s. Null
        // for all other platforms (no stream-key viewer-count API).
        viewer_count?: number | null;
        viewer_count_at?: string | null;
      }>
    >(`/api/v1/meetings/${meetingId}/stream/destinations`),

  // YouTube OAuth — managed-mode connect/disconnect/status. Start
  // returns the authorize URL as JSON so the SPA can carry the bearer
  // token; the SPA then `window.open()`s the URL in a popup and waits
  // for the callback page to `postMessage` back.
  youtubeOauthStart: (meetingId: string) =>
    request<{ authorize_url: string }>(
      `/api/v1/meetings/${meetingId}/youtube/oauth/start`,
      { method: "POST" },
    ),
  youtubeOauthStatus: (meetingId: string) =>
    request<{
      connected: boolean;
      channel_title: string | null;
      channel_id: string | null;
      mode: "rtmp" | "api";
      watch_url: string | null;
      broadcast_id: string | null;
    }>(`/api/v1/meetings/${meetingId}/youtube/oauth/status`),
  youtubeOauthDisconnect: (meetingId: string) =>
    request<{ ok: boolean }>(
      `/api/v1/meetings/${meetingId}/youtube/oauth/disconnect`,
      { method: "POST" },
    ),

  listRecordings: () => request<unknown[]>("/api/v1/recordings"),

  listMeetingRecordings: (meetingId: string) =>
    request<unknown[]>(`/api/v1/meetings/${meetingId}/recordings`),

  downloadTranscript: async (recordingId: string, filename: string) => {
    const tok = getAccessToken() ?? (await bootstrapFromOneWitysk());
    const headers: HeadersInit = tok ? { Authorization: `Bearer ${tok}` } : {};
    const r = await fetch(`/api/v1/recordings/${recordingId}/transcript`, { headers });
    if (!r.ok) {
      throw new Error(`Transcript download failed: ${r.status}`);
    }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  },

  publishYoutube: (
    recordingId: string,
    body: { title?: string; description?: string; privacy?: "public" | "unlisted" | "private" } = {}
  ) =>
    request<{ ok: boolean; url: string; video_id: string }>(
      `/api/v1/recordings/${recordingId}/publish-youtube`,
      { method: "POST", body: JSON.stringify(body) }
    ),

  /**
   * Download a recording. The endpoint requires a Bearer token, which a
   * plain `<a href>` cannot carry — so we fetch the file with the
   * `Authorization` header, materialise a Blob URL, and trigger a save via
   * a synthesised anchor click. The server's `Content-Disposition` is
   * preserved as the suggested filename if present; otherwise `fallbackName`
   * is used.
   *
   * Caveat: the whole file ends up in browser memory. For typical meeting
   * lengths this is fine (~few hundred MB). For multi-GB files, switch to a
   * service-worker-streamed download or a short-lived signed URL.
   */
  async downloadRecording(recordingId: string, fallbackName: string): Promise<void> {
    const tok = getAccessToken();
    let res = await fetch(`/api/v1/recordings/${recordingId}/download`, {
      headers: tok ? { Authorization: `Bearer ${tok}` } : {},
    });
    if (res.status === 401) {
      // Cached token may be stale; clear it and force the bootstrap to
      // re-fetch the current value from one.witysk.org's localStorage.
      clearAccessToken();
      const fresh = await forceBootstrapFromOneWitysk();
      if (fresh) {
        res = await fetch(`/api/v1/recordings/${recordingId}/download`, {
          headers: { Authorization: `Bearer ${fresh}` },
        });
      }
    }
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const data = await res.clone().json();
        detail = data.detail ?? detail;
      } catch {
        /* not JSON */
      }
      throw new Error(detail);
    }

    // Pull a filename out of Content-Disposition if the server set one.
    const cd = res.headers.get("Content-Disposition") || "";
    const m = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
    const filename = m ? decodeURIComponent(m[1].replace(/"$/, "")) : fallbackName;

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Give the browser a tick to start the download before revoking.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  deleteRecording: (recordingId: string) =>
    request(`/api/v1/recordings/${recordingId}`, { method: "DELETE" }),

  invite: (
    meetingId: string,
    body: { emails: string[]; personal_message?: string; display_name?: string | null }
  ) =>
    request<{ ok: boolean; sent: number; failed: string[]; join_url: string }>(
      `/api/v1/meetings/${meetingId}/invite`,
      { method: "POST", body: JSON.stringify(body) }
    ),

  /** Public: meeting metadata for the lobby (no auth). */
  publicRoomInfo: (roomName: string) =>
    request<PublicRoomInfo>(`/api/v1/rooms/${roomName}/info`),

  /** Public view-only stream — lookup by public slug. No auth. */
  publicViewerInfo: (publicSlug: string) =>
    request<PublicRoomInfoForViewer>(
      `/api/v1/public-room/${encodeURIComponent(publicSlug)}`,
    ),

  publicViewerToken: (
    publicSlug: string,
    body: { display_name?: string } = {},
  ) =>
    request<PublicViewerTokenResponse>(
      `/api/v1/public-room/${encodeURIComponent(publicSlug)}/viewer-token`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  getMyPreferences: () =>
    request<UserPreferencesOut>("/api/v1/me/preferences"),

  updateMyPreferences: (body: {
    language?: string;
    anonymise_email_in_join_log?: boolean;
    dont_log_my_ip?: boolean;
  }) =>
    request<UserPreferencesOut>("/api/v1/me/preferences", {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  // ─── Admin panel ─────────────────────────────────────────────────────
  adminListUsers: (
    params: {
      q?: string;
      kind?: "sso" | "native";
      limit?: number;
      offset?: number;
      sort_by?: "id" | "kind" | "email" | "username" | "name" | "created_at";
      sort_order?: "asc" | "desc";
    } = {}
  ) => {
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    if (params.kind) qs.set("kind", params.kind);
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.offset != null) qs.set("offset", String(params.offset));
    if (params.sort_by) qs.set("sort_by", params.sort_by);
    if (params.sort_order) qs.set("sort_order", params.sort_order);
    const tail = qs.toString();
    return request<AdminUserList>(`/api/v1/admin/users${tail ? `?${tail}` : ""}`);
  },

  adminGetUser: (userId: number) =>
    request<AdminUserOut>(`/api/v1/admin/users/${userId}`),

  adminUpdateUser: (
    userId: number,
    body: { is_platform_admin?: boolean; is_disabled?: boolean; disable_reason?: string | null }
  ) =>
    request<AdminUserOut>(`/api/v1/admin/users/${userId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  adminSetPassword: (userId: number, new_password: string) =>
    request<{ ok: boolean }>(`/api/v1/admin/users/${userId}/set-password`, {
      method: "POST",
      body: JSON.stringify({ new_password }),
    }),

  adminDeleteUser: (userId: number) =>
    request<{ ok: boolean }>(`/api/v1/admin/users/${userId}`, { method: "DELETE" }),

  adminListBlockedIps: () => request<BlockedIPOut[]>("/api/v1/admin/blocked-ips"),

  adminAddBlockedIp: (body: { ip_address: string; reason?: string | null }) =>
    request<BlockedIPOut>("/api/v1/admin/blocked-ips", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  adminUpdateBlockedIp: (id: number, body: { is_enabled?: boolean; reason?: string | null }) =>
    request<BlockedIPOut>(`/api/v1/admin/blocked-ips/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  adminDeleteBlockedIp: (id: number) =>
    request<{ ok: boolean }>(`/api/v1/admin/blocked-ips/${id}`, { method: "DELETE" }),

  adminIdsStatus: () => request<IdsStatusOut>("/api/v1/admin/ids/status"),

  adminIdsEvents: (params: { limit?: number; persisted?: boolean } = {}) => {
    const qs = new URLSearchParams();
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.persisted) qs.set("persisted", "true");
    const tail = qs.toString();
    return request<IdsEvent[]>(`/api/v1/admin/ids/events${tail ? `?${tail}` : ""}`);
  },

  adminIdsUnblock: (ip: string) =>
    request<{ ok: boolean; was_blocked: boolean }>(
      `/api/v1/admin/ids/unblock/${encodeURIComponent(ip)}`,
      { method: "POST" }
    ),

  /** Upload (or replace) a meeting's branding image. */
  async uploadBranding(meetingId: string, file: File): Promise<{ branding_url: string | null }> {
    const tok = getAccessToken();
    const fd = new FormData();
    fd.append("file", file);
    let res = await fetch(`/api/v1/meetings/${meetingId}/branding`, {
      method: "POST",
      headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      body: fd,
    });
    if (res.status === 401) {
      clearAccessToken();
      const fresh = await bootstrapFromOneWitysk();
      if (fresh) {
        res = await fetch(`/api/v1/meetings/${meetingId}/branding`, {
          method: "POST",
          headers: { Authorization: `Bearer ${fresh}` },
          body: fd,
        });
      }
    }
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const j = await res.clone().json();
        detail = j.detail ?? detail;
      } catch {
        /* ignore */
      }
      throw new Error(detail);
    }
    return res.json();
  },

  // ─── Video playback (LiveKit Ingress URL_INPUT) ─────────────────────

  listPlaybackItems: (meetingId: string) =>
    request<PlaybackItemOut[]>(`/api/v1/meetings/${meetingId}/playback/items`),

  /** Multipart upload of one MP4 to the meeting's playlist. Streamed
   *  on the server so a 400 MB file doesn't sit in memory. We also
   *  probe the video's duration in the SPA via HTMLVideoElement and
   *  send it as a form field so the playback panel can render a
   *  progress bar without ffprobe on the server. */
  async uploadPlaybackItem(
    meetingId: string,
    file: File,
    displayName?: string,
    durationSeconds?: number,
  ): Promise<PlaybackItemOut> {
    const tok = getAccessToken();
    const fd = new FormData();
    fd.append("file", file);
    if (displayName) fd.append("filename", displayName);
    if (durationSeconds && Number.isFinite(durationSeconds) && durationSeconds > 0) {
      fd.append("duration_seconds", String(durationSeconds));
    }
    let res = await fetch(`/api/v1/meetings/${meetingId}/playback/items`, {
      method: "POST",
      headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      body: fd,
    });
    if (res.status === 401) {
      clearAccessToken();
      const fresh = await bootstrapFromOneWitysk();
      if (fresh) {
        res = await fetch(`/api/v1/meetings/${meetingId}/playback/items`, {
          method: "POST",
          headers: { Authorization: `Bearer ${fresh}` },
          body: fd,
        });
      }
    }
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const j = await res.clone().json();
        detail = j.detail ?? detail;
      } catch {
        /* ignore */
      }
      throw new Error(detail);
    }
    return res.json();
  },

  deletePlaybackItem: (meetingId: string, itemId: string) =>
    request<{ ok: boolean }>(`/api/v1/meetings/${meetingId}/playback/items/${itemId}`, { method: "DELETE" }),

  /** Rename a playlist item's display label. Server caps at 200 chars
   *  and rejects blank names; on success the updated item is returned
   *  so callers can refresh in-place without a full list refetch. */
  renamePlaybackItem: (meetingId: string, itemId: string, filename: string) =>
    request<PlaybackItemOut>(
      `/api/v1/meetings/${meetingId}/playback/items/${itemId}`,
      { method: "PATCH", body: JSON.stringify({ filename }) },
    ),

  /** Create an ALIAS to an existing playlist item. Appears at the end
   *  of the playlist; reorder normally. Plays the same file as the
   *  source without duplicating it on disk. */
  duplicatePlaybackItem: (meetingId: string, itemId: string) =>
    request<PlaybackItemOut>(
      `/api/v1/meetings/${meetingId}/playback/items/${itemId}:duplicate`,
      { method: "POST" },
    ),

  /** Download a previously-uploaded playlist item. The endpoint
   *  requires a Bearer token (owner / co-host) so an `<a href>` won't
   *  work — same fetch-as-blob pattern as `downloadRecording`. Alias
   *  rows resolve to the source's file on the backend. */
  async downloadPlaybackItem(meetingId: string, itemId: string, filename: string): Promise<void> {
    const tok = getAccessToken();
    let res = await fetch(`/api/v1/meetings/${meetingId}/playback/items/${itemId}/download`, {
      headers: tok ? { Authorization: `Bearer ${tok}` } : {},
    });
    if (res.status === 401) {
      clearAccessToken();
      const fresh = await bootstrapFromOneWitysk();
      if (fresh) {
        res = await fetch(`/api/v1/meetings/${meetingId}/playback/items/${itemId}/download`, {
          headers: { Authorization: `Bearer ${fresh}` },
        });
      }
    }
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const j = await res.clone().json();
        detail = j.detail ?? detail;
      } catch {
        /* ignore */
      }
      throw new Error(detail);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  },

  reorderPlaybackItems: (meetingId: string, itemIds: string[]) =>
    request<PlaybackItemOut[]>(
      `/api/v1/meetings/${meetingId}/playback/items:reorder`,
      { method: "PUT", body: JSON.stringify({ item_ids: itemIds }) },
    ),

  startPlayback: (meetingId: string) =>
    request<{ ok: boolean; ingress_id: string; item_id: string }>(
      `/api/v1/meetings/${meetingId}/playback:start`,
      { method: "POST" },
    ),

  /** Jump to a specific item in the playlist — switches the source
   *  without ending the playback session if one's already running. */
  playPlaybackItem: (meetingId: string, itemId: string) =>
    request<{ ok: boolean; ingress_id: string; item_id: string }>(
      `/api/v1/meetings/${meetingId}/playback/items/${itemId}:play`,
      { method: "POST" },
    ),

  /** Current playback state — polled by the side panel for the
   *  progress bar and current-item highlight. */
  playbackState: (meetingId: string) =>
    request<PlaybackStateOut>(`/api/v1/meetings/${meetingId}/playback`),

  stopPlayback: (meetingId: string) =>
    request<{ ok: boolean; already_stopped?: boolean }>(
      `/api/v1/meetings/${meetingId}/playback:stop`,
      { method: "POST" },
    ),

  /** Pause without ending: server swaps the running ingress for a
   *  frozen-frame loop at the current offset so every viewer keeps
   *  seeing the same still image. Resume picks up at the same offset. */
  pausePlayback: (meetingId: string) =>
    request<{ ok: boolean; already_paused?: boolean; offset_seconds?: number }>(
      `/api/v1/meetings/${meetingId}/playback:pause`,
      { method: "POST" },
    ),

  /** Inverse of pausePlayback — restart the real ingress at the saved
   *  paused offset. */
  resumePlayback: (meetingId: string) =>
    request<{ ok: boolean; offset_seconds?: number }>(
      `/api/v1/meetings/${meetingId}/playback:resume`,
      { method: "POST" },
    ),

  /** Drag-to-seek the current item to `positionSeconds`. The server
   *  restarts the ingress at the requested offset; participants see a
   *  brief gap as the new ingress reconnects. */
  seekPlayback: (meetingId: string, positionSeconds: number) =>
    request<{ ok: boolean; ingress_id: string; position_seconds: number }>(
      `/api/v1/meetings/${meetingId}/playback:seek`,
      {
        method: "POST",
        body: JSON.stringify({ position_seconds: positionSeconds }),
      },
    ),
};
